// S12 — THE INSTRUMENT WALL (spec 04 §4 floor row 12, §5 e17, §9.16;
// design-system §3.11 and reference screen 03).
//
// THIS SCREEN IS FURNITURE. It is taped to a tablet or bolted to a TV in a
// corridor, nobody signs into it, nobody taps it, and the people it is for are
// WALKING PAST at three or four metres. Every decision below falls out of that:
//
//  * NO INTERACTION, ANYWHERE. No filters, no picker, no pagination, no
//    buttons. A control on a wall board is a control that gets knocked by a
//    passing shoulder and leaves the board on someone else's shift. The facility
//    is whatever the session can see, and the SHIFT is whichever one is running
//    — which is not the same as "today" and cost this screen a measured lie
//    before it was (see the two-date derivation below).
//  * LEGIBLE AT THREE METRES. The output numeral is the largest thing on the
//    glass at `--text-inst` (42 px), which is the register's ABSOLUTE ceiling;
//    labels are small, uppercase and recessive. At that distance a caption is
//    noise and a number is the message.
//  * ONE SCREENFUL, NEVER A SCROLLBAR — AND THE WORST STATION IS NEVER THE ONE
//    LEFT OFF. Nobody scrolls a wall. The board shows the stations that FIT and
//    says how many it left out, rather than hiding them silently below a fold
//    nobody will ever reach; and because the worst-yield tile is this board's
//    only ranked signal, `wallVisibleRows` guarantees that tile a slot instead
//    of letting the truncation delete it. This is enforced in THREE places,
//    because the first two versions of it were promises rather than mechanisms:
//    the shell is `height: 100dvh; overflow: hidden` (styles/floor.css
//    `.app-wall`), the board is a four-band grid whose tiles band is the only
//    flexible one, and the tile budget is MEASURED off the viewport
//    (`wallTileBudget`) instead of being a constant that fit no particular
//    screen.
//  * IT DEGRADES HONESTLY (§9.16). When the poll fails the board KEEPS the last
//    numbers — a blank wall tells a plant nothing — but it stamps them with
//    their age and turns the stamp loud. A confident wrong number on a wall is
//    worse than no number at all: a supervisor makes a decision off it and
//    nothing on the glass said it was an hour old.
//  * 60-SECOND POLL (§9.16's floor). `useShiftScoreboard` already polls at that
//    cadence; the SSE stream is a latency upgrade and this screen is deliberately
//    correct without it.
//
// ===========================================================================
// REBUILT AT UX-5 onto the locked register (design-system §3.11, reference
// screen 03) — and what it replaced is worth recording, because it had been
// live for weeks and no test saw it.
//
// The old board was DARK: `background: var(--floor-bg, #0f1319)` with `#eef2f6`
// text, `#ff5c47` alarm tints and `clamp()` numerals that reached 96 px on a
// wide panel. Dark canvases and poster numerals are both on the §0 permanent
// rejection record. It survived because NONE of the four `--floor-*` variables
// it was painted with has been defined anywhere since `app.css` was dissolved
// at UX-4 — every one silently fell through to its dark literal fallback — and
// because the gate's dark-canvas check (6c) reads token DEFINITIONS in
// tokens.css, so a dark canvas smuggled in as a `var()` FALLBACK inside an area
// stylesheet scored as an ordinary ratchet-counted raw colour.
//
// What is here now is reference screen 03: a light instrument panel, white
// tiles on the `--bg` canvas, the semantic trio for meaning only, the yield
// split bar, a worded diagnosis rather than a tint alone, and a totals band.
//
// WHAT THE REFERENCE HAS AND THIS BOARD DOES NOT, and why — none of it is an
// omission of nerve, all of it is the CONTRACT. `ShiftScoreboardRow` (spec 04
// §5 e17) carries exactly: qty_good, qty_scrap, qty_rework,
// first_pass_yield_pct, downtime_minutes, defects_*, worker_count, finalized,
// and the shift's identity. Reference screen 03 additionally shows a SKU, a
// plan ("/ 10 000"), a rate against a target ("987 м/ч · цель 833"), a machine
// state (РАБОТАЕТ / НАЛАДКА / ПРОСТОЙ), a named operator, a drum number and a
// worded downtime CAUSE ("Обрыв жилы"). Not one of those is on the wire, and a
// wall board is the last surface in the product that may invent a number. So
// each is either rendered from what IS known (the state chip states what the
// row knows — this shift is open, or the close job has finalized it; the
// diagnosis states the downtime MINUTES, which are real, and not a cause, which
// is not) or left off. The gap is reported as a module-04 finding rather than
// papered over here.
//
// AND THE SAME RULE DECIDES THE UNIT — which this board does not have and does
// not invent (UX-5 surface review; recorded in spec 04 §4 row 12). `qty_good`,
// `qty_scrap` and `qty_rework` arrive as `numeric(14,3)` decimal strings with
// NO `uom` field anywhere on `ShiftScoreboardRow` or on
// `ShiftScoreboardFacilityTotal`, and the projection aggregates a work centre's
// whole shift, which may legitimately span operations measured differently — so
// there is not necessarily one unit to print even if the wire carried one. The
// board renders bare numbers. Stamping "m" on them because cable is the design
// partner's product would be this screen inventing the most consequential digit
// on the tile: a 42 px numeral read at three metres by whoever walks past,
// labelled with a unit nothing on the wire supports. The office S5 scoreboard
// has the identical gap and renders the identical bare numbers, so the two
// surfaces at least agree. The fix is e17 carrying a `uom` (null when a shift
// mixed them) — a contract change, logged as successor work.
//
// AUTH IS AN ORDINARY FLOOR SESSION (D-087 ruling 1). §5 e17's role column
// named a "wall-mode token"; no scoped-token machinery exists anywhere on this
// platform, so the interim is D-079's shape — the tablet is signed in as a
// floor principal holding `production.scoreboard.read`, the RLS facility fence
// narrows it to its own plant, and the scoped token is named as
// module-18-adjacent successor work. `floor_devices` is never an auth factor
// (D-079 pt 3): a QR taped to a tablet in a corridor is not a credential.

import { useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import {
  useShiftScoreboard,
  type ShiftScoreboardRow,
} from "../../office/production/hooks/production-queries";
import type { components } from "@assessly/api-client";
import { facilityTodayIso } from "../../../lib/business-dates";
import { formatBusinessDate } from "../../../lib/datetime";
import { worstYieldRowKey, rowKey } from "../../office/production/screens/shifts";
import { useWorkCenters, useFacilities } from "../../office/production/hooks/production-queries";
import { formatQty, localeLabel, parseQty } from "../../office/production/lib/format";
import "./floor-production.css";

type FacilityTotal = components["schemas"]["ShiftScoreboardFacilityTotal"];

// ---- THE TILE BUDGET ------------------------------------------------------
// These numbers are DUPLICATED in `floor-production.css` (the grid's
// `minmax()` floor, the tile row height, the gap and the board's padding), and
// the duplication is deliberate: the budget has to be the same arithmetic the
// browser is about to do, or the count and the layout disagree and the board
// scrolls anyway. They are exported so a test can assert the pair rather than
// trust the comment.

/** The grid's `minmax()` floor. Sized so a 1280–1420 px panel lands on the
 * reference's 3-up grid: below ~380 px a tile cannot carry a 42 px numeral, a
 * state chip and a three-fact footer without one of them wrapping. */
export const WALL_TILE_MIN_PX = 380;
/** The tile's height at the fixed type sizes below, measured at its TALLEST —
 * a station carrying both the downtime strip and the LOWEST badge. Budgeting
 * off the shortest tile is how a board ends up clipping the one tile that has
 * something to say. Re-measured at UX-5's surface fix: every caption on the
 * tile stepped up one or two scale steps for the three-metre read (D-120), and
 * the station name may now take two lines instead of being clipped. */
export const WALL_TILE_ROW_PX = 216;
/** `gap` between tiles and between bands (`--space-3`). */
export const WALL_GAP_PX = 12;
/** The board's own padding, both sides. */
export const WALL_PAD_PX = 32;
/** The head band, the TOTALS band and the "+N more" band — the three grid rows
 * the tiles do NOT get, plus the gaps between all four. Grew from 132 at UX-5
 * when the totals band landed; a chrome figure that has not been re-measured
 * after a band was added is how a footer ends up below the fold. */
export const WALL_CHROME_PX = 202;
/** Below this width the totals band is 3-up instead of 5-up (five 23 px
 * numerals do not fit five columns of a 412 px phone), which makes it two rows
 * tall. Duplicated in `floor-production.css`'s media query. */
export const WALL_NARROW_PX = 700;
/** Below this width the band drops again, 3-up → 2-up (three rows). Found on
 * staging by the responsive net, through the DATA rather than the layout: a
 * six-digit shift total at the band's 24 px numeral is ~87 px, and a 412 px
 * phone's 3-up column holds ~79 — the number clipped with nothing scrollable
 * around it. Real factories put seven digits on this band (a cable line's
 * daily metres), so the fix is a wider box, not a smaller number: at 2-up a
 * column carries ~10 digits at full size. Duplicated in
 * `floor-production.css`'s media query, same as the 3-up breakpoint. */
export const WALL_PHONE_PX = 480;
/** The height one extra row of totals tiles costs, gap included. */
export const WALL_TOTAL_ROW_PX = 75;
/** The extra line the staleness stamp may wrap onto below `WALL_PHONE_PX`.
 * The stamp is `white-space: nowrap` at wall widths — one pill, one line — but
 * a 412 px phone genuinely cannot hold the ru stamp beside the clock: "ОНЛАЙН ·
 * Обновлено N с назад" measures 281 px against the ~260 px the head-right row
 * can give it, so the head CLIPPED at ru@412 (W-SWEEP, 2026-08-14 — scrollWidth
 * 385 in a 378 px head with nothing scrollable around it). Dropping any word
 * was not an option — §9.16 makes the stamp's loudness load-bearing — so below
 * the phone breakpoint the pill keeps every word and WRAPS instead
 * (`floor-production.css`, same `WALL_PHONE_PX` media query), and this constant
 * charges the chrome for the line the wrap can add. Charged by WIDTH, not by
 * locale, exactly like the totals-band rows: the budget cannot know the
 * language on the glass, so it prices the tallest honest head. */
export const WALL_STAMP_WRAP_PX = 20;
/** Hard ceiling. Past a dozen tiles nothing on the glass is legible from the
 * corridor anyway, so the board counts the rest rather than shrinking itself
 * into a spreadsheet nobody can read while walking. */
export const WALL_TILE_MAX = 12;

/**
 * The chrome this width actually wears.
 *
 * A constant was wrong the moment the totals band learned to reflow: at 412 px
 * it is two rows, not one, and a budget still counting on one row would hand the
 * tile grid a band it does not have and push the "+N more" footer off the glass
 * — the exact failure the measured budget replaced a constant `8` to prevent,
 * arriving through the WIDTH instead of the height.
 */
export function wallChromePx(width: number): number {
  // Below the phone breakpoint the totals band is 2-up (two extra rows) AND
  // the staleness stamp may wrap to a second line (`WALL_STAMP_WRAP_PX` — the
  // CSS that lets it is behind the same breakpoint).
  if (width < WALL_PHONE_PX) return WALL_CHROME_PX + 2 * WALL_TOTAL_ROW_PX + WALL_STAMP_WRAP_PX;
  return width < WALL_NARROW_PX ? WALL_CHROME_PX + WALL_TOTAL_ROW_PX : WALL_CHROME_PX;
}

/**
 * How many tiles fit THIS viewport, and in how many columns.
 *
 * The first version was `WALL_TILE_LIMIT = 8`, a constant calibrated to no
 * particular screen — which is how a 1280×800 wall tablet ended up with a
 * `scrollHeight` of 1250 and its "+N more stations" footer ~450 px below a fold
 * nobody scrolls. The count comes from the glass.
 */
export function wallTileBudget(width: number, height: number): { cols: number; tiles: number } {
  const usableW = Math.max(0, width - WALL_PAD_PX);
  const usableH = Math.max(0, height - WALL_PAD_PX - wallChromePx(width));
  const cols = Math.max(1, Math.floor((usableW + WALL_GAP_PX) / (WALL_TILE_MIN_PX + WALL_GAP_PX)));
  const rows = Math.max(1, Math.floor((usableH + WALL_GAP_PX) / (WALL_TILE_ROW_PX + WALL_GAP_PX)));
  return { cols, tiles: Math.min(WALL_TILE_MAX, cols * rows) };
}

/**
 * WHICH TILES GO ON THE GLASS — and the rule that the WORST STATION IS NEVER
 * ONE OF THE ONES LEFT OFF.
 *
 * The budget above answers "how many fit". It does not answer "which", and for
 * most of this screen's life it did not have to: `rows.slice(0, budget)` in
 * server order is fine when everything fits. The moment it does not, plain
 * slicing throws away the only thing this board is for. The worst-yield tile is
 * the board's SINGLE RANKED SIGNAL — the alarm border and the worded LOWEST
 * badge both hang off it — and a station ranked worst but sitting past the
 * budget took that signal off the wall entirely: no tile marked, no badge,
 * nothing worded, and a "+3 more stations" footer that says how many were
 * dropped but not that one of them was the one you needed. The board looked
 * healthiest exactly when the plant was biggest, which is the opposite of the
 * truth.
 *
 * The fix keeps SERVER ORDER — furniture must not reshuffle under a passing
 * glance, and sorting by yield would move every tile whenever a number moved —
 * and simply guarantees the worst row a slot: if it fell outside the budget it
 * takes the LAST visible one. The overflow count is unchanged (one row is still
 * one row), so the footer keeps telling the truth.
 */
export function wallVisibleRows(
  rows: readonly ShiftScoreboardRow[],
  budget: number,
  worstKey: string | null,
): ShiftScoreboardRow[] {
  const visible = rows.slice(0, Math.max(0, budget));
  if (worstKey === null || visible.length === 0) return visible;
  if (visible.some((r) => rowKey(r) === worstKey)) return visible;
  const worst = rows.find((r) => rowKey(r) === worstKey);
  if (worst === undefined) return visible;
  return [...visible.slice(0, visible.length - 1), worst];
}

/**
 * THE SHIFT ON THE FLOOR, out of the two calendar dates that can carry it.
 *
 * `finalized` is exactly the right discriminator and it is already on the wire:
 * §3.7's close job sets it when it recomputes a shift from source, so an
 * UNFINALIZED row is by definition a shift nobody has closed — the one being
 * worked. When some rows are live the board shows those and only those; when
 * none are (between shifts, or after the tick has closed everything), it shows
 * what there is rather than going blank, because a wall with yesterday's
 * numbers and an honest age stamp beats a wall with nothing.
 */
export function wallLiveRows(rows: readonly ShiftScoreboardRow[]): ShiftScoreboardRow[] {
  const live = rows.filter((r) => !r.finalized);
  return live.length > 0 ? live : [...rows];
}

/**
 * THE TOTALS BAND'S NUMBERS COME OFF THE WIRE, NEVER OFF THIS SCREEN.
 *
 * §3.11 owes a 5-up totals row, and there are two ways to fill it. One is to
 * sum the rows the board is drawing. That is the wrong one, and not marginally:
 * `first_pass_yield_pct` is a GENERATED column (migration 0028) computed as
 * `(good − rework) / (good + scrap) × 100`, null when the denominator is zero,
 * rounded half-away-from-zero at three decimals — and a client that re-derived
 * it would be the THIRD implementation of a definition the database owns, free
 * to drift from the office's S5 totals in the fourth decimal today and in the
 * first one after any change to the formula. The office screen reads the
 * server's `facility_totals` for exactly this reason; so does this one.
 *
 * The catch is that `facility_totals` is scoped per REQUESTED DATE while the
 * tiles are scoped per RUNNING SHIFT, and those are the same set only when one
 * date carries the live rows. That is the ordinary case (a shift runs inside
 * one shift_date by construction), so:
 *
 *   · one date carries the live rows → its server totals, labelled with it;
 *   · two do (a changeover tick with both shifts open) → NULL, and the band
 *     says so in words. Showing one date's totals under tiles drawn from two
 *     would be a number that disagrees with the tiles above it, which is the
 *     one thing a wall board must never do.
 */
export function wallTotals(
  responses: readonly { shift_date: string; facility_totals: readonly FacilityTotal[] }[],
  facilityId: string | undefined,
  liveRows: readonly ShiftScoreboardRow[],
): { date: string; total: FacilityTotal } | null {
  if (facilityId === undefined || liveRows.length === 0) return null;
  const dates = new Set(liveRows.map((r) => r.shift_date));
  if (dates.size !== 1) return null;
  const [date] = [...dates];
  if (date === undefined) return null;
  const response = responses.find((r) => r.shift_date === date);
  const total = response?.facility_totals.find((f) => f.facility_id === facilityId);
  return total === undefined ? null : { date, total };
}

/**
 * The yield split bar's two segments, as percentages of `good + scrap`.
 *
 * This is a RATIO FOR A BAR, not a restatement of `first_pass_yield_pct` — it
 * deliberately does not subtract rework, because a bar is a picture of what came
 * off the machine and the percentage beside it is the shift's yield statistic.
 * Rendering the generated column as a bar width would put two subtly different
 * definitions of "yield" on the same tile. Null when nothing was made: an empty
 * track, not a full green one.
 */
export function wallYieldSplit(
  qtyGood: string,
  qtyScrap: string,
): { goodPct: number; scrapPct: number } | null {
  const good = parseQty(qtyGood);
  const scrap = parseQty(qtyScrap);
  if (good === undefined || scrap === undefined) return null;
  const total = good + scrap;
  if (total <= 0n) return null;
  const goodPct = Number((good * 10_000n) / total) / 100;
  return { goodPct, scrapPct: 100 - goodPct };
}

/** Older than this and the staleness stamp goes loud. Two poll intervals: one
 * missed poll is a blip, two is a network the board must stop pretending
 * about. */
export const WALL_STALE_MS = 120_000;

export function wallAgeSeconds(asOf: string | undefined, now: number): number | null {
  if (asOf === undefined) return null;
  const at = Date.parse(asOf);
  if (Number.isNaN(at)) return null;
  return Math.max(0, Math.floor((now - at) / 1000));
}

/**
 * The age, in the unit a person walking past can actually parse.
 *
 * It used to render raw seconds, which is honest and useless: three hours came
 * out as **"Updated 10805s ago"**, and the whole job of this stamp is to be
 * understood at a glance by somebody who is not stopping. Seconds up to 90
 * (a fresh board really is seconds old), then minutes, then hours.
 */
export function wallAgeLabel(seconds: number): {
  unit: "Seconds" | "Minutes" | "Hours";
  n: number;
} {
  if (seconds < 90) return { unit: "Seconds", n: seconds };
  if (seconds < 90 * 60) return { unit: "Minutes", n: Math.round(seconds / 60) };
  return { unit: "Hours", n: Math.round(seconds / 3600) };
}

/**
 * THE CLOCK IS THE PLANT'S, not the tablet's.
 *
 * Same rule as the polled dates below, and the same reason: this glass hangs in
 * a corridor in Almaty, and the one thing a supervisor uses the clock for is to
 * ask "how much of the shift is left". A tablet with a mis-set OS timezone —
 * which is most tablets that came out of a box in a different country — would
 * answer that question wrong all day while looking authoritative. Hours and
 * minutes only: the board's own data moves on a 60-second poll, so a seconds
 * hand would be the only thing on the wall that is ever up to date.
 */
export function wallClock(
  nowMs: number,
  timezone: string | null | undefined,
  locale: string,
): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      ...(timezone !== null && timezone !== undefined && timezone.length > 0
        ? { timeZone: timezone }
        : {}),
    }).format(nowMs);
  } catch {
    // An unknown IANA zone must not take the board down; the clock falls back
    // to the device's, which is still better than a blank header.
    return new Intl.DateTimeFormat(locale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(nowMs);
  }
}

export function WallBoardScreen() {
  const { t, i18n } = useTranslation();
  const facilities = useFacilities();
  // The whole facility, not just its id: the board's two polled dates AND its
  // clock are derived in ITS calendar (`timezone`), never the viewer's — see
  // the date block below.
  const facility = facilities.data?.items[0];
  const facilityId = facility?.id;
  // NO status filter. A work centre archived mid-shift still has a scoreboard
  // row for the shift it worked, and an "active only" lookup rendered that row
  // with a `—` where its name goes — the board silently losing the identity of
  // a station that is still on it. Naming is a lookup, not a permission.
  const workCenters = useWorkCenters(facilityId);

  // A ticking clock so the staleness stamp AGES on screen even when no poll
  // comes back. Without it a dead network leaves "updated 0s ago" frozen on the
  // wall, which is the exact lie this stamp exists to prevent. It is ALSO what
  // rolls the board's own dates over, and what moves the header clock — one
  // timer, three jobs, and no seconds hand to justify a faster one.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const handle = setInterval(() => {
      setNow(Date.now());
    }, 5_000);
    return () => {
      clearInterval(handle);
    };
  }, []);

  // TWO DATES, DERIVED FROM THE TICKING CLOCK — and this is the difference
  // between a board and a liar.
  //
  // §9.7 dates a shift by the day it STARTS, so the night shift running at
  // 05:00 is dated YESTERDAY. A board that asked for `today` alone therefore
  // rendered "Nothing reported yet on this shift" — in words, on a wall, at
  // three metres — while twenty-two populated rows sat on the very floor it was
  // bolted to, for the whole first half of every night shift. It has no
  // controls (correctly), so nobody walking past could fix it. That is exactly
  // the confident-wrong-number failure the staleness stamp exists to prevent,
  // arriving through the date instead of through the clock.
  //
  // Two calendar dates are ENOUGH, not a guess: a shift window cannot exceed 24
  // hours, so a shift running now started either today or yesterday.
  //
  // They come off `now` rather than a `useState` initialiser for the mirror-
  // image bug: the date used to be captured once, at mount, so a board left up
  // overnight — which is every wall board — kept asking for the date it booted
  // on, forever.
  //
  // AND THEY ARE THE FACILITY'S DATES, NOT THE VIEWER'S (fixed 2026-08-08).
  // §9.7's `shift_date` is facility-local, and this screen derived both dates
  // with `todayIso` — the browser calendar. Any viewer west of the plant (a
  // wall tablet with a mis-set OS timezone; the UTC container the staging
  // smoke runs in) therefore polled {facility-date−2, facility-date−1} for
  // the first hours of every facility day and missed the running shift's date
  // entirely — stale tiles under a fresh "updated seconds ago" stamp, on a
  // screen with no control to correct it. M04-S07 caught it on staging at
  // 00:07 Almaty; the API and the walkthrough were both right, this
  // derivation was the defect. The polls are enabled only once the facility
  // (and so its timezone) is in hand, so the browser-calendar fallback inside
  // `facilityTodayIso` never actually decides a wall poll.
  const shiftDate = facilityTodayIso(now, facility?.timezone);
  const priorDate = facilityTodayIso(now - 86_400_000, facility?.timezone);
  const today = useShiftScoreboard(
    { shift_date: shiftDate, ...(facilityId !== undefined ? { facility_id: facilityId } : {}) },
    facilityId !== undefined,
  );
  const prior = useShiftScoreboard(
    { shift_date: priorDate, ...(facilityId !== undefined ? { facility_id: facilityId } : {}) },
    facilityId !== undefined,
  );

  // THE LAST GOOD PAYLOAD, kept across failures. §9.16: "renders last cached
  // scoreboard with a staleness stamp" — a blank wall tells a plant nothing.
  // Both polls feed it, and it is only replaced when BOTH have answered, so a
  // half-refreshed board never drops the shift that happens to be running. The
  // TOTALS ride along with the rows they belong to, so a failed poll cannot
  // leave fresh totals over stale tiles.
  const [lastGood, setLastGood] = useState<{
    rows: ShiftScoreboardRow[];
    asOf: string;
    responses: { shift_date: string; facility_totals: FacilityTotal[] }[];
  } | null>(null);
  useEffect(() => {
    if (today.data !== undefined && prior.data !== undefined) {
      setLastGood({
        // Yesterday first: a running night shift started there, and server
        // order within each answer is preserved.
        rows: wallLiveRows([...prior.data.rows, ...today.data.rows]),
        asOf: today.data.as_of,
        responses: [
          { shift_date: prior.data.shift_date, facility_totals: prior.data.facility_totals },
          { shift_date: today.data.shift_date, facility_totals: today.data.facility_totals },
        ],
      });
    }
  }, [today.data, prior.data]);

  // THE BUDGET COMES FROM THE GLASS, and follows it: a wall tablet gets rotated
  // and a panel gets swapped, and neither should push the footer off-screen.
  const [budget, setBudget] = useState(() =>
    wallTileBudget(
      typeof window === "undefined" ? 1280 : window.innerWidth,
      typeof window === "undefined" ? 800 : window.innerHeight,
    ),
  );
  useEffect(() => {
    const measure = (): void => {
      setBudget(wallTileBudget(window.innerWidth, window.innerHeight));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("resize", measure);
    };
  }, []);

  const rows = lastGood?.rows ?? [];
  const ageSeconds = wallAgeSeconds(lastGood?.asOf, now);
  const stale = ageSeconds !== null && ageSeconds * 1000 >= WALL_STALE_MS;
  const worstKey = worstYieldRowKey(rows);
  const visible = wallVisibleRows(rows, budget.tiles, worstKey);
  const hidden = rows.length - visible.length;
  const age = ageSeconds === null ? null : wallAgeLabel(ageSeconds);
  const totals = wallTotals(lastGood?.responses ?? [], facilityId, rows);

  // WHAT MAKES TWO TILES DIFFERENT, and it is the PAIR (F2, UX-5 review).
  // The tile caption used to key on `shift_definition_id` alone while the
  // totals band suppressed itself on the DATE — so a board carrying one shift
  // code across two business dates (the ordinary night-shift changeover, and
  // the permanent state of any plant whose close job missed a tick) drew six
  // uncaptioned tiles under a message saying two shifts were open. One tile can
  // differ from another by its shift OR by its date, so the caption keys on
  // both, and it appears exactly when the board is carrying more than one.
  const visibleShiftDates = [...new Set(visible.map((r) => r.shift_date))];
  const scopeOf = (r: ShiftScoreboardRow): string => `${r.shift_definition_id} ${r.shift_date}`;
  const multiShift = new Set(visible.map(scopeOf)).size > 1;
  const multiDate = visibleShiftDates.length > 1;

  // The scope line under the plant name: which shift, on which business date.
  // Derived from the rows ON THE GLASS — `visible`, not `rows` — because the
  // claim the comment makes ("it can never name a shift the board is not
  // showing") is only true of the tiles that survived the budget. With ten
  // stations hidden, `rows` names shifts nobody can see a tile for.
  const shiftCodes = [...new Set(visible.map((r) => r.shift_definition_code))];
  const shiftDates = visibleShiftDates;

  const stationName = (id: string): string => {
    const wc = workCenters.data?.items.find((c) => c.id === id);
    return wc === undefined ? "—" : localeLabel(wc.name_i18n, i18n.language) || wc.code;
  };

  const qty = (value: string, maxDp = 0): string =>
    formatQty(value, { locale: i18n.language, maxDp });

  return (
    <section className="wall-board" data-testid="wall-board">
      <header className="wall-head">
        <h1 className="wall-plant">{facility?.name ?? t("production.floor.wall.title")}</h1>
        <p className="wall-scope" data-testid="wall-scope">
          {shiftCodes.length > 0
            ? t("production.floor.wall.scope", {
                shift: shiftCodes.join(" · "),
                // Rendered, never the raw ISO wire date — the same rule the
                // tile captions follow (src/lib/datetime.ts, one renderer).
                date: shiftDates.map((d) => formatBusinessDate(d, i18n.language)).join(" · "),
              })
            : t("production.floor.wall.title")}
        </p>
        <div className="wall-head-right">
          {/* The LIVE chip and the staleness stamp are ONE element, because they
              are one fact in two states. Reference screen 03 draws a bare LIVE
              chip; §9.16 requires the age. Two SEPARATE indicators would let a
              board show a green LIVE badge beside an hour-old timestamp, which
              is the exact confident-wrong-number failure the stamp exists to
              stop — so the word and the age ride together, and the word is what
              flips. */}
          <p
            className={
              age === null
                ? "wall-stamp wall-stamp-waiting"
                : stale
                  ? "wall-stamp wall-stamp-stale"
                  : "wall-stamp"
            }
            data-testid="wall-stamp"
            data-stale={stale ? "true" : "false"}
            role="status"
          >
            {age === null
              ? t("production.floor.wall.neverLoaded")
              : `${stale ? t("production.floor.wall.stale") : t("production.floor.wall.live")} · ${t(
                  `production.floor.wall.updatedAgo${age.unit}`,
                  { n: age.n },
                )}`}
          </p>
          <span className="wall-clock" data-testid="wall-clock">
            {wallClock(now, facility?.timezone, i18n.language)}
          </span>
        </div>
      </header>

      {rows.length === 0 ? (
        <p className="wall-empty" data-testid="wall-empty">
          {t("production.floor.wall.empty")}
        </p>
      ) : (
        <ul
          className="wall-tiles"
          data-testid="wall-tiles"
          data-cols={budget.cols}
          style={{ "--wall-cols": budget.cols } as CSSProperties}
        >
          {visible.map((row) => {
            const worst = rowKey(row) === worstKey;
            const split = wallYieldSplit(row.qty_good, row.qty_scrap);
            return (
              <li
                key={rowKey(row)}
                className={worst ? "wall-tile wall-tile-worst" : "wall-tile"}
                data-testid="wall-tile"
                data-work-center={row.work_center_id}
              >
                <div className="wall-tile-top">
                  <span className="wall-tile-station">{stationName(row.work_center_id)}</span>
                  {multiShift ? (
                    <span className="wall-tile-shift" data-testid="wall-tile-shift">
                      {/* The date rides RENDERED (F2 decided that it rides;
                          nothing decided it rides as ISO). `shift_date` goes
                          through the app's one date renderer, exactly like
                          station-home's copy of the same field — the wire's
                          `2026-08-06` never reaches the glass (the my-shift
                          rule). */}
                      {multiDate
                        ? `${row.shift_definition_code} · ${formatBusinessDate(
                            row.shift_date,
                            i18n.language,
                          )}`
                        : row.shift_definition_code}
                    </span>
                  ) : null}
                  {/* WHAT THE ROW KNOWS, and nothing more. The reference's
                      РАБОТАЕТ / НАЛАДКА / ПРОСТОЙ is a MACHINE state; the wire
                      carries a SHIFT state (`finalized`), so that is what the
                      chip says. Inventing a running/setup/down reading from a
                      downtime minute count would be a guess rendered at 3 m. */}
                  <span
                    className={
                      row.finalized ? "wall-chip wall-chip-closed" : "wall-chip wall-chip-run"
                    }
                    data-testid="wall-tile-state"
                  >
                    {row.finalized
                      ? t("production.floor.wall.stateClosed")
                      : t("production.floor.wall.stateOpen")}
                  </span>
                </div>

                <span className="wall-tile-label">{t("production.floor.wall.outputLabel")}</span>

                {/* FORMATTED, like every other surface that shows this exact
                    value. The wire is `1234.000`; at 42 px those three trailing
                    zeros are the widest thing on the tile and carry no
                    information, and S5 renders the identical number as
                    `1,234`. A wall board is the LAST place to make a reader
                    work harder than the office screen does. */}
                <span className="wall-tile-good" data-testid="wall-tile-good">
                  {qty(row.qty_good)}
                </span>

                {/* The yield split bar (reference `.ybar`). An empty track when
                    nothing has been made — a full green bar over a zero output
                    would read as a perfect shift. */}
                <span className="wall-ybar" aria-hidden="true">
                  {split === null ? null : (
                    <>
                      <span className="wall-ybar-good" style={{ width: `${split.goodPct}%` }} />
                      {split.scrapPct > 0 ? (
                        <span className="wall-ybar-bad" style={{ width: `${split.scrapPct}%` }} />
                      ) : null}
                    </>
                  )}
                </span>

                <p className="wall-yrow">
                  <b className="wall-tile-fpy" data-testid="wall-tile-fpy">
                    {row.first_pass_yield_pct === null
                      ? t("production.floor.wall.noYield")
                      : `${qty(row.first_pass_yield_pct, 1)}%`}
                  </b>
                  <span>{t("production.floor.wall.fpyLabel")}</span>
                  {/* Worded, not just bordered: a wall board is read by whoever
                      walks past, including the third of them who cannot tell
                      this tint from the tile beside it. */}
                  {worst ? (
                    <span className="wall-tile-badge" data-testid="wall-tile-badge">
                      {t("production.floor.wall.worst")}
                    </span>
                  ) : null}
                </p>

                {/* The reference's worded diagnosis strip, carrying the one
                    diagnosis the wire actually has. Minutes, not a cause: §9.11's
                    downtime rows are real and their REASON is not on e17. */}
                {row.downtime_minutes > 0 ? (
                  <p className="wall-diag" data-testid="wall-tile-diag">
                    {t("production.floor.wall.downtime", { n: row.downtime_minutes })}
                  </p>
                ) : null}

                <p className="wall-tile-foot">
                  <span>
                    {t("production.floor.wall.footScrap")} <b>{qty(row.qty_scrap)}</b>
                  </span>
                  <span>
                    {t("production.floor.wall.footRework")} <b>{qty(row.qty_rework)}</b>
                  </span>
                  <span>
                    {t("production.floor.wall.footWorkers")} <b>{row.worker_count}</b>
                  </span>
                </p>
              </li>
            );
          })}
        </ul>
      )}

      {/* THE TOTALS BAND (§3.11's 5-up row). Server numbers or none — see
          `wallTotals`. */}
      {rows.length === 0 ? null : totals === null ? (
        <p className="wall-more" data-testid="wall-totals-unavailable">
          {/* The message names the condition that ACTUALLY suppressed the band
              — two business dates, which is what `facility_totals` is scoped
              by — and names them. "Two shifts are open" was a different
              sentence from the code's own test (F2). */}
          {t("production.floor.wall.totalsMixed", {
            dates: [...new Set(rows.map((r) => r.shift_date))]
              .map((d) => formatBusinessDate(d, i18n.language))
              .join(" · "),
          })}
        </p>
      ) : (
        <ul className="wall-totals" data-testid="wall-totals">
          <li className="wall-total">
            <span className="wall-total-label">{t("production.floor.wall.totalOutput")}</span>
            <span className="wall-total-value" data-testid="wall-total-good">
              {qty(totals.total.qty_good)}
            </span>
          </li>
          <li className="wall-total">
            <span className="wall-total-label">{t("production.floor.wall.totalYield")}</span>
            <span className="wall-total-value" data-testid="wall-total-fpy">
              {totals.total.first_pass_yield_pct === null
                ? t("production.floor.wall.noYield")
                : `${qty(totals.total.first_pass_yield_pct, 1)}%`}
            </span>
          </li>
          <li className="wall-total">
            <span className="wall-total-label">{t("production.floor.wall.totalScrap")}</span>
            <span className="wall-total-value">{qty(totals.total.qty_scrap)}</span>
          </li>
          <li className="wall-total">
            <span className="wall-total-label">{t("production.floor.wall.totalRework")}</span>
            <span className="wall-total-value">{qty(totals.total.qty_rework)}</span>
          </li>
          <li className="wall-total">
            <span className="wall-total-label">{t("production.floor.wall.totalDowntime")}</span>
            <span className="wall-total-value">
              {totals.total.downtime_minutes}
              <small> {t("production.floor.wall.minutesUnit")}</small>
            </span>
          </li>
        </ul>
      )}

      {hidden > 0 ? (
        <p className="wall-more" data-testid="wall-more">
          {t("production.floor.wall.more", { n: hidden })}
        </p>
      ) : null}
    </section>
  );
}
